// aws-onboarding.js
import isEqual from "lodash/isEqual";
import {
  POPULAR_AWS_REGION_IDS,
  SLINGBOT_ACCOUNT_ID,
} from "onboarding/constants";

export const INITIAL_STATE = {
  // values from environment or database that aren't user-modifiable
  accountId: SLINGBOT_ACCOUNT_ID, // id of AWS account used by slingbot
  externalId: null, // should be auto-generated when integration is created
  integrationId: null,
  mutationErrors: [],
  nameTaken: false,
  nameTagTaken: false,
  regions: [],
  services: [],

  // user inputs that get persisted
  accountNameTag: "", // e.g. staging
  arn: "", // e.g. arn:aws:iam::123456789012:role/AppOptics
  integrationName: "", // e.g. AppOptics CloudWatch
  selectedRegions: [],
  selectedServices: [],

  // ui state
  connected: false,
  countdown: 20,
  disableBilling: false,
  disabledBilling: false,
  disablePopular: false,
  initialized: false,
  queryId: null,
  showAll: false,
  timerStarted: false,
};

// helpers
export const ROLE_ARN_REGEX = /^arn:aws[^:]*:iam:[^:]*:\d{12}:role\/[\w+=,.@/-]+$/;

export const validRoleArn = potentialArn => {
  // General formats for ARNs:
  // arn:partition:service:region:account-id:resource
  // arn:partition:service:region:account-id:resourcetype/resource
  // arn:partition:service:region:account-id:resourcetype:resource
  //
  // e.g. arn:aws:iam::123456789012:role/AppOptics

  return ROLE_ARN_REGEX.test(potentialArn || "");
};

const AWS_CHINA_REGION = "cn-north-1";
const detectUnpopularRegions = state => {
  const { selectedRegions } = state;
  const disablePopular = selectedRegions.reduce(
    (unpopular, id) => unpopular || POPULAR_AWS_REGION_IDS.indexOf(id) < 0,
    false
  );
  const showAll = state.showAll || disablePopular;
  const disableOthers = selectedRegions.indexOf(AWS_CHINA_REGION) >= 0;
  const disableChina = !disableOthers && selectedRegions.length > 0;
  return {
    ...state,
    disablePopular,
    showAll,
    disableChina,
    disableOthers,
  };
};

export const serializeAwsState = (state = {}, compareTo = {}) => {
  const { accountNameTag, arn, integrationName } = state;
  const { selectedRegions, selectedServices } = validateServicePrereq(state);
  const [anyRegion] = selectedRegions;
  const [anyService] = selectedServices;
  const validArn = validRoleArn(arn);
  const enabled = integrationName && anyRegion && anyService && validArn;

  const awsIntegration = {};
  if (!state.initialized) {
    // If the user hasn't made any changes, just ensure it's enabled.
    if (enabled && !compareTo.enabled) awsIntegration.enabled = true;
    return awsIntegration;
  }

  if (integrationName && integrationName !== compareTo.name) {
    awsIntegration.name = integrationName;
  }
  if (accountNameTag && accountNameTag !== compareTo.accountNameTag) {
    awsIntegration.accountNameTag = accountNameTag;
  }
  if (arn && arn !== compareTo.arn) {
    awsIntegration.arn = arn;
  }

  if (!isEqual(selectedRegions, compareTo.selectedRegions)) {
    awsIntegration.regions = selectedRegions.map(id => ({ id, enabled: true }));
  }
  const filteredServices = compareTo.filteredServices || [];
  if (!isEqual(selectedServices, compareTo.services)) {
    awsIntegration.services = selectedServices.map(id => {
      const filter = filteredServices.indexOf(id) >= 0;
      return { id, enabled: true, filter };
    });
  }

  if (enabled !== compareTo.enabled) {
    awsIntegration.enabled = enabled;
  }

  return awsIntegration;
};

export const deserializeAwsState = (awsIntegration, state = INITIAL_STATE) => {
  if (!Object.keys(awsIntegration || {}).length) return state;
  const {
    id: integrationId,
    name: integrationName,
    accountNameTag,
    arn,
    externalId,
    regions,
    services,
    selectedRegions,
    selectedServices,
  } = awsIntegration;
  return {
    ...state,
    arn,
    externalId,
    accountNameTag,
    integrationId,
    integrationName,
    regions,
    services,
    ...detectUnpopularRegions(
      validateServicePrereq({ selectedRegions, selectedServices })
    ),
  };
};

export const BILLING_PREREQ = { region: "us-east-1", service: "AWS/Billing" };
const validateServicePrereq = state => {
  const { selectedRegions, selectedServices, disabledBilling } = state || {};
  const validated = {
    selectedRegions: [...selectedRegions],
    selectedServices: [...selectedServices],
    disableBilling: selectedRegions.indexOf(BILLING_PREREQ.region) < 0,
  };
  const billingIndex = selectedServices.indexOf(BILLING_PREREQ.service);
  if (validated.disableBilling) {
    // automatically disable the billing service when region is disabled
    if (billingIndex >= 0) {
      validated.selectedServices.splice(billingIndex, 1);
      validated.disabledBilling = true;
    }
  } else if (disabledBilling) {
    if (billingIndex < 0) {
      // automatically re-enable the billing service when region is re-enabled
      validated.selectedServices.push(BILLING_PREREQ.service);
    }
    validated.disabledBilling = false;
  }
  validated.selectedRegions.sort();
  validated.selectedServices.sort();
  return validated;
};
const toggle = (currentSelectedOptions = [], option, selected) => {
  const selectedOptions = [...currentSelectedOptions];
  if (!option) return selectedOptions;
  const optionIndex = selectedOptions.indexOf(option);
  if (optionIndex >= 0) {
    if (!selected) {
      selectedOptions.splice(optionIndex, 1);
    }
  } else if (selected !== false) {
    selectedOptions.push(option);
  }
  selectedOptions.sort();
  return selectedOptions;
};
const toggleRegionSelected = (state, { region, selected }) => {
  return validateServicePrereq({
    ...state,
    selectedRegions: toggle(state.selectedRegions, region, selected),
  });
};
const toggleServiceSelected = (state, { service, selected }) => {
  return validateServicePrereq({
    ...state,
    selectedServices: toggle(state.selectedServices, service, selected),
  });
};

const REGION_TOOLTIP_REASON =
  "AWS China (Beijing) Region requires a unique separate set of account credentials";
const REGION_TOOLTIP_RESULT =
  "cannot be enabled while cn-north-1 (Beijing, China) is selected.";
const REGION_TOOLTIP_CHINA = `The ${REGION_TOOLTIP_REASON}, so it cannot be enabled while other regions are selected.`;
const REGION_TOOLTIP_OTHER = `The ${REGION_TOOLTIP_REASON}, so this region ${REGION_TOOLTIP_RESULT}`;
const BILLING_TOOLTIP_REGION_DISABLED = `Region us-east-1 must be enabled to import Billing metrics, but the ${REGION_TOOLTIP_REASON}, so the us-east-1 region ${REGION_TOOLTIP_RESULT}`;
const BILLING_TOOLTIP =
  "Region us-east-1 must be enabled to import Billing metrics. Billing metrics must also be activated in CloudWatch before they can be imported into AppOptics.";

const mapRegions = (
  regions,
  { selectedRegions, disableChina, disableOthers }
) =>
  regions.map(region => {
    const tooltip =
      region.id === AWS_CHINA_REGION
        ? disableChina && REGION_TOOLTIP_CHINA
        : disableOthers && REGION_TOOLTIP_OTHER;
    return {
      ...region,
      selected: selectedRegions.indexOf(region.id) >= 0,
      disabled: !!tooltip,
      tooltip,
    };
  });

const mapServices = (
  services,
  { selectedServices, disableBilling, disableOthers }
) =>
  services.map(service => {
    const disabled = disableBilling && service.id === BILLING_PREREQ.service;
    const tooltip =
      disabled &&
      (disableOthers ? BILLING_TOOLTIP_REGION_DISABLED : BILLING_TOOLTIP);
    return {
      ...service,
      selected: selectedServices.indexOf(service.id) >= 0,
      disabled,
      tooltip,
    };
  });

const sortRegions = regions =>
  regions.sort(
    (regionA, regionB) =>
      regionA.id < regionB.id ? -1 : regionA.id > regionB.id ? 1 : 0
  );

const sortServices = services =>
  services.sort(
    (serviceA, serviceB) =>
      serviceA.id < serviceB.id ? -1 : serviceA.id > serviceB.id ? 1 : 0
  );

const MUTATION_ERRORS = {
  nameTaken: "name and user_id and provider is already taken",
  nameTagTaken: "source_prefix and user_id and provider is already taken",
};
const parseMutationErrors = mutationErrors => {
  const errors = mutationErrors || [];
  const parsedErrors = { mutationErrors };
  for (let errorKey in MUTATION_ERRORS) {
    parsedErrors[errorKey] = errors.indexOf(MUTATION_ERRORS[errorKey]) >= 0;
  }
  return parsedErrors;
};

// Replicate `svc.split("/").last.underscore` from the metrics app
const templateForServiceId = id =>
  id
    .replace(/^.*?\//, "")
    // adapted from https://github.com/rails/rails/blob/ac717d6/…
    //  …activesupport/lib/active_support/inflector/methods.rb#L96-L99
    .replace(/([A-Z\d]+)([A-Z][a-z])/g, "$1_$2")
    .replace(/([a-z\d])([A-Z])/g, "$1_$2")
    .replace(/-/g, "_")
    .toLowerCase();

// selectors
export const getRegions = ({
  onboarding: { aws: { regions, showAll, ...awsState } },
}) => {
  const displayedRegions = showAll
    ? regions
    : regions.filter(({ id }) => POPULAR_AWS_REGION_IDS.indexOf(id) >= 0);
  return sortRegions(mapRegions(displayedRegions, awsState));
};

export const getServices = ({ onboarding: { aws: awsState } }) =>
  sortServices(mapServices(awsState.services, awsState));

export const getSelectedServiceTemplates = ({
  onboarding: { aws: { selectedServices } },
}) =>
  selectedServices
    .map(id => templateForServiceId(id))
    .filter(template => template !== "custom");

// action types
const SET_AWS_QUERY_ID = "appoptics/aws-onboarding/SET_AWS_QUERY_ID";
const SET_AWS_ONBOARDING_STATE =
  "appoptics/aws-onboarding/SET_AWS_ONBOARDING_STATE";
const SET_INTEGRATION_NAME = "appoptics/aws-onboarding/SET_INTEGRATION_NAME";
const SET_ACCOUNT_NAME_TAG = "appoptics/aws-onboarding/SET_ACCOUNT_NAME_TAG";
const SET_MUTATION_ERRORS = "appoptics/aws-onboarding/SET_MUTATION_ERRORS";
const SET_ARN = "appoptics/aws-onboarding/SET_ARN";
const SHOW_ALL_REGIONS = "appoptics/aws-onboarding/SHOW_ALL_REGIONS";
const TOGGLE_REGION = "appoptics/aws-onboarding/TOGGLE_REGION";
const TOGGLE_SERVICE = "appoptics/aws-onboarding/TOGGLE_SERVICE";
const SET_CONNECTED = "appoptics/aws-onboarding/SET_CONNECTED";
const TICK = "appoptics/aws-onboarding/TICK";

// action creators
export const setAwsIntegrationQueryId = queryId => ({
  type: SET_AWS_QUERY_ID,
  queryId,
});

export const setAwsOnboardingState = integration => ({
  type: SET_AWS_ONBOARDING_STATE,
  integration,
});

export const setIntegrationName = integrationName => ({
  type: SET_INTEGRATION_NAME,
  integrationName,
});

export const setAccountNameTag = accountNameTag => ({
  type: SET_ACCOUNT_NAME_TAG,
  accountNameTag,
});

export const setMutationErrors = mutationErrors => ({
  type: SET_MUTATION_ERRORS,
  mutationErrors: mutationErrors || [],
});

export const setArn = arn => ({
  type: SET_ARN,
  arn,
});

export const toggleRegion = (region, selected) => ({
  type: TOGGLE_REGION,
  region,
  selected,
});

export const toggleService = (service, selected) => ({
  type: TOGGLE_SERVICE,
  service,
  selected,
});

export const setConnected = (connected = true) => ({
  type: SET_CONNECTED,
  connected,
});

export const showAllRegions = () => ({
  type: SHOW_ALL_REGIONS,
  showAll: true,
});

export const showPopularRegions = () => ({
  type: SHOW_ALL_REGIONS,
  showAll: false,
});

export const tick = () => ({
  type: TICK,
});

const formatTagName = (value = "") => {
  return value
    .toLowerCase() // automatically convert to lowercase
    .replace(/ /g, "-") // automatically convert spaces to hyphens
    .replace(/[^a-z0-9.:_-]/g, ""); // remove remaining invalid characters
};

// reducer
const awsOnboardingReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case SET_AWS_QUERY_ID:
      return {
        ...state,
        queryId: action.queryId,
      };
    case SET_AWS_ONBOARDING_STATE:
      return deserializeAwsState(action.integration, state);
    case SET_INTEGRATION_NAME: {
      const integrationName = action.integrationName || "";
      const accountNameTag = formatTagName(integrationName);
      return {
        ...state,
        integrationName,
        accountNameTag,
        nameTaken: false,
        nameTagTaken: false,
        initialized: true,
      };
    }
    case SET_ACCOUNT_NAME_TAG: {
      const accountNameTag = formatTagName(action.accountNameTag);
      return {
        ...state,
        accountNameTag,
        nameTagTaken: false,
        initialized: true,
      };
    }
    case SET_MUTATION_ERRORS:
      return {
        ...state,
        ...parseMutationErrors(action.mutationErrors),
        initialized: true,
      };
    case SET_ARN:
      return {
        ...state,
        arn: (action.arn || "").replace(/ /g, ""),
        initialized: true,
      };
    case SHOW_ALL_REGIONS:
      return {
        ...state,
        showAll: action.showAll,
        initialized: true,
      };
    case TOGGLE_REGION:
      return detectUnpopularRegions({
        ...state,
        ...toggleRegionSelected(state, action),
        initialized: true,
      });
    case TOGGLE_SERVICE:
      return {
        ...state,
        ...toggleServiceSelected(state, action),
        initialized: true,
      };
    case SET_CONNECTED:
      return {
        ...state,
        connected: action.connected,
        initialized: true,
      };
    case TICK:
      return {
        ...state,
        countdown: state.countdown < 1 ? 0 : state.countdown - 1,
        timerStarted: true,
        initialized: true,
      };
    default:
      return state;
  }
};
export default awsOnboardingReducer;
